import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { Warehouse, Truck, CheckCircle2, AlertTriangle, Euro, ClipboardList, TrendingUp, TrendingDown, Minus, Target, Car, Trophy, Activity, HelpCircle, X } from "lucide-react";
import { BarChart, Bar as ChartBar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { startAppTour } from "@/lib/tour";
import { toast } from "sonner";

function AlertDeleteBtn({ id, show, onDelete }) {
  if (!show) return null;
  return (
    <button type="button" onClick={() => onDelete(id)} title="Elimina alert" className="absolute right-2 top-2 rounded-sm p-1 text-slate-400 transition-colors hover:bg-white hover:text-red-600" data-testid={`alert-delete-${id}`}>
      <X className="h-3.5 w-3.5" />
    </button>
  );
}

const Kpi = ({ icon: Icon, label, value, tone = "default", testid }) => {
  const toneClass =
    tone === "warning" ? "text-orange-600" :
    tone === "danger" ? "text-red-600" :
    tone === "success" ? "text-emerald-600" : "text-slate-900";
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
        <Icon className={`h-4 w-4 ${toneClass}`} />
      </div>
      <div className={`mt-3 font-mono text-3xl font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
};

const DeltaBadge = ({ value }) => {
  if (value === null || value === undefined) {
    return (
      <span className="inline-flex items-center gap-1 rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
        <Minus className="h-3 w-3" /> —
      </span>
    );
  }
  const up = value >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${
        up ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
      }`}
      data-testid="delta-badge"
    >
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? "+" : ""}{value}%
    </span>
  );
};

const Bar = ({ label, value, max, color, dashed = false }) => {
  const pct = Math.min(100, (value / (max || 1)) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="w-10 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="h-2 flex-1 overflow-hidden rounded-sm bg-slate-100">
        <div className={`h-full ${color} ${dashed ? "opacity-60" : ""}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="w-24 text-right font-mono text-xs font-semibold text-slate-900">€ {Number(value).toFixed(2)}</div>
    </div>
  );
};

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const canSeeRevenue = user?.role === "admin";
  const isTecnico = user?.role === "tecnico";
  const [stats, setStats] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [prodByComm, setProdByComm] = useState(null);
  // Filtro custom per la card "Produzione per committente"
  const [prodFrom, setProdFrom] = useState("");
  const [prodTo, setProdTo] = useState("");
  const [prodLoading, setProdLoading] = useState(false);
  const [vehicleAlerts, setVehicleAlerts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [history, setHistory] = useState(null);
  const [myKpi, setMyKpi] = useState(null);

  // Agenda alerts (eventi domani/in corso oggi)
  const [agendaAlerts, setAgendaAlerts] = useState([]);
  const [dismissedAgenda, setDismissedAgenda] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem("agenda_dismiss") || "[]")); } catch { return new Set(); }
  });
  const dismissAgenda = (id) => {
    const s = new Set(dismissedAgenda); s.add(id);
    setDismissedAgenda(s);
    try { sessionStorage.setItem("agenda_dismiss", JSON.stringify([...s])); } catch {}
  };
  useEffect(() => {
    api.get("/agenda-events/upcoming").then((r) => setAgendaAlerts(r.data || [])).catch(() => {});
  }, []);

  const loadProdByComm = async (from = "", to = "") => {
    setProdLoading(true);
    try {
      const params = {};
      if (from && to) { params.date_from = from; params.date_to = to; }
      const r = await api.get("/dashboard/production-by-committente", { params });
      setProdByComm(r.data);
    } catch (e) { /* ignore */ }
    finally { setProdLoading(false); }
  };

  const applyProdFilter = () => {
    if (!prodFrom || !prodTo) return;
    loadProdByComm(prodFrom, prodTo);
  };
  const resetProdFilter = () => {
    setProdFrom(""); setProdTo("");
    loadProdByComm();
  };

  const loadStats = () => api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  const [allAlerts, setAllAlerts] = useState(null);
  const loadAllAlerts = () => api.get("/alerts").then((r) => setAllAlerts(r.data || [])).catch(() => toast.error("Caricamento alert non riuscito"));
  const toggleAllAlerts = () => (allAlerts ? setAllAlerts(null) : loadAllAlerts());
  const canDeleteAlerts = user?.role === "admin" || user?.role === "magazziniere";
  const deleteAlert = async (id) => {
    try {
      await api.delete(`/alerts/${id}`);
      setAllAlerts((list) => (list ? list.filter((a) => a.id !== id) : list));
      loadStats();
    } catch { toast.error("Eliminazione non riuscita"); }
  };
  const deleteAllAlerts = async () => {
    if (!window.confirm("Eliminare tutti gli alert?")) return;
    try { await api.delete("/alerts"); toast.success("Alert eliminati"); setAllAlerts(null); loadStats(); } catch { toast.error("Eliminazione non riuscita"); }
  };

  useEffect(() => {
    loadStats();
    if (canSeeRevenue) {
      api.get("/dashboard/ranking").then((r) => setRanking(r.data.ranking)).catch(() => {});
      loadProdByComm();
    }
    api.get("/vehicle-alerts").then((r) => setVehicleAlerts(r.data)).catch(() => {});
    if (!isTecnico) {
      api.get("/materials/low-stock").then((r) => setLowStock(r.data)).catch(() => {});
    } else {
      api.get("/dashboard/earnings-history", { params: { months: 6 } }).then((r) => setHistory(r.data)).catch(() => {});
      // Ultimi 30 giorni di KPI (assegnati vs chiusi)
      api.get("/performance/kpi").then((r) => setMyKpi(r.data)).catch(() => {});
    }
  }, [canSeeRevenue, isTecnico]);

  if (!stats) return <div className="text-slate-500">{t("dashboard.loading")}</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("dashboard.overview")}</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("dashboard.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("dashboard.subtitle")}</p>
        </div>
        {user?.role === "admin" && (
          <button
            onClick={() => startAppTour({ lang: i18n.language })}
            className="inline-flex items-center gap-2 rounded-sm border border-[#0052FF] bg-white px-3 py-2 text-sm font-semibold text-[#0052FF] hover:bg-[#0052FF] hover:text-white transition-colors"
            data-testid="btn-start-tour"
          >
            <HelpCircle className="h-4 w-4" />
            {t("dashboard.guide_me")}
          </button>
        )}
      </div>

      {/* Alert Agenda: eventi in corso oggi o che iniziano nei prossimi 3 giorni */}
      {agendaAlerts.filter((e) => !dismissedAgenda.has(e.id)).length > 0 && (
        <div className="space-y-2" data-testid="agenda-alerts">
          {agendaAlerts.filter((e) => !dismissedAgenda.has(e.id)).map((ev) => {
            const dLeft = typeof ev.days_until_start === "number" ? ev.days_until_start : null;
            const bg = ev.type === "assenza" ? "border-amber-400 bg-amber-50 text-amber-900"
              : ev.type === "task"      ? "border-blue-400  bg-blue-50  text-blue-900"
              : ev.type === "riunione"  ? "border-purple-400 bg-purple-50 text-purple-900"
              :                           "border-slate-400 bg-slate-50 text-slate-900";
            const who = ev.assigned_to_names?.length ? ev.assigned_to_names.join(", ") : "team";
            const preavvisoLabel =
              dLeft === null ? "📌 In corso" :
              dLeft <= 0 ? "📌 In corso oggi" :
              dLeft === 1 ? "⏰ Domani" :
              dLeft === 2 ? "⏰ Dopodomani" :
              `⏰ Tra ${dLeft} giorni`;
            return (
              <div key={ev.id} className={`flex items-start justify-between gap-3 rounded-sm border-l-4 p-3 ${bg}`}>
                <div className="flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-widest">
                    {preavvisoLabel} · {ev.type.toUpperCase()}
                  </div>
                  <div className="mt-0.5 text-sm font-bold">{ev.title}</div>
                  <div className="mt-0.5 text-xs opacity-80">
                    {ev.date_from}{ev.date_to && ev.date_to !== ev.date_from ? ` → ${ev.date_to}` : ""} · <b>{who}</b>
                    {ev.description ? ` — ${ev.description}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => dismissAgenda(ev.id)}
                  className="rounded-sm p-1 hover:bg-black/10"
                  data-testid={`agenda-dismiss-${ev.id}`}
                  title="Nascondi (torna al prossimo login)"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {lowStock.length > 0 && (
        <div className="rounded-sm border-2 border-red-400 bg-red-50 p-4" data-testid="lowstock-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <div className="text-sm font-bold uppercase tracking-wider text-red-900">{t("dashboard.low_stock")} ({lowStock.length})</div>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
            {lowStock.map((m) => (
              <div key={m.id} className="rounded-sm border border-red-200 bg-white p-2 text-sm">
                <div className="font-semibold text-slate-900">{m.name}</div>
                <div className="mt-0.5 font-mono text-xs">
                  <span className="text-red-600 font-bold">{Number(m.stock_qty || 0).toFixed(1)} {m.unit}</span>
                  <span className="text-slate-400"> / {t("dashboard.threshold")} {Number(m.min_threshold).toFixed(1)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {vehicleAlerts.length > 0 && (
        <div className="rounded-sm border-2 border-orange-400 bg-orange-50 p-4" data-testid="vehicle-alerts-banner">
          <div className="mb-2 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <div className="text-sm font-bold uppercase tracking-wide text-orange-900">
              {t("dashboard.vehicle_expirations")} ({vehicleAlerts.length})
            </div>
            <Link to="/automezzi" className={`ml-auto text-xs font-semibold text-[#0052FF] hover:underline ${isTecnico ? "hidden" : ""}`}>{t("dashboard.go_to_vehicles")} →</Link>
          </div>
          <div className="space-y-1.5">
            {vehicleAlerts.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-2 rounded-sm border border-orange-200 bg-white p-2 text-sm" data-testid={`vehicle-alert-${a.id}`}>
                <Car className="h-4 w-4 text-orange-700" />
                <span className="font-mono font-bold text-slate-900">{a.vehicle?.plate || "—"}</span>
                <span className="text-slate-600">·</span>
                <span className="font-semibold uppercase tracking-wide text-orange-800">{a.type}</span>
                <span className="text-slate-500">scad. {a.due_date}</span>
                <span className={`ml-auto rounded-sm border px-2 py-0.5 text-[10px] font-bold ${
                  a.overdue
                    ? "border-red-300 bg-red-100 text-red-800"
                    : "border-orange-300 bg-orange-100 text-orange-900"
                }`}>
                  {a.overdue ? t("dashboard.expired_by", { days: Math.abs(a.days_left) }) : t("dashboard.expires_in", { days: a.days_left })}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[11px] italic text-orange-700" dangerouslySetInnerHTML={{ __html: t("dashboard.vehicle_alerts_note") }} />
        </div>
      )}

      <div className={`grid grid-cols-2 gap-4 ${isTecnico ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
        <Kpi icon={Warehouse} label={isTecnico ? t("dashboard.my_warehouse_modems") : t("dashboard.warehouse_general")} value={stats.serials.warehouse} testid="kpi-warehouse" />
        <Kpi icon={Truck} label={isTecnico ? t("dashboard.my_travel_modems") : t("dashboard.in_travel")} value={stats.serials.traveling} tone="warning" testid="kpi-traveling" />
        <Kpi icon={CheckCircle2} label={isTecnico ? t("dashboard.my_installed_modems") : t("dashboard.installed")} value={stats.serials.installed} tone="success" testid="kpi-installed" />
        {!isTecnico && (
          <Kpi icon={AlertTriangle} label={t("dashboard.serial_alerts")} value={stats.alerts_count} tone={stats.alerts_count > 0 ? "danger" : "default"} testid="kpi-alerts" />
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {(canSeeRevenue || isTecnico) && (
          <div className="rounded-sm border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="production-card">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
              {isTecnico ? t("dashboard.my_production_month") : t("dashboard.monthly_production")}
            </div>
            <div className="mt-4 flex items-end justify-between">
              <div>
                <div className="font-mono text-5xl font-semibold text-slate-900" data-testid="production-total">€ {stats.monthly.total.toFixed(2)}</div>
                <div className="mt-1 text-sm text-slate-500">
                  {isTecnico
                    ? `${stats.monthly.count} ${t("dashboard.works_split_note")}`
                    : `${stats.monthly.count} ${t("dashboard.consuntivated_works")}`}
                </div>
              </div>
              <div className={`grid h-16 w-16 place-items-center rounded-sm ${isTecnico ? "bg-emerald-600" : "bg-[#0052FF]"} text-white`}>
                <Euro className="h-6 w-6" />
              </div>
            </div>
            {isTecnico && stats.monthly_goal > 0 && (() => {
              const g = stats.monthly_goal;
              const t = stats.monthly.total;
              const pct = Math.max(0, Math.min(100, (t / g) * 100));
              const remaining = Math.max(0, g - t);
              const reached = t >= g;
              return (
                <div className="mt-5 rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="goal-progress">
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1 font-bold uppercase tracking-widest text-slate-600">
                      <Target className="h-3.5 w-3.5" /> Meta mensile
                    </div>
                    <div className="font-mono text-slate-700">
                      € {t.toFixed(2)} / <b>€ {g.toFixed(2)}</b>
                    </div>
                  </div>
                  <div className="h-3 w-full overflow-hidden rounded-sm bg-slate-200">
                    <div
                      className={`h-full transition-all ${reached ? "bg-emerald-500" : pct >= 66 ? "bg-blue-500" : pct >= 33 ? "bg-amber-400" : "bg-red-400"}`}
                      style={{ width: `${pct}%` }}
                      data-testid="goal-bar"
                    />
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[11px]">
                    <div className="font-bold text-slate-700" data-testid="goal-pct">{pct.toFixed(0)}%</div>
                    {reached ? (
                      <div className="inline-flex items-center gap-1 rounded-sm bg-emerald-100 px-1.5 py-0.5 font-bold uppercase text-emerald-800" data-testid="goal-reached">
                        <Trophy className="h-3 w-3" /> Meta raggiunta!
                      </div>
                    ) : (
                      <div className="text-slate-500" data-testid="goal-remaining">
                        Mancano <b className="text-slate-900">€ {remaining.toFixed(2)}</b>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {!isTecnico && (
        <div className={`rounded-sm border border-slate-200 bg-white p-6 ${canSeeRevenue ? "" : "lg:col-span-3"}`} data-testid="alerts-card">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.alert_recenti_kicker")}{stats.alerts_count ? ` (${stats.alerts_count})` : ""}</div>
            <div className="flex items-center gap-2">
              {canDeleteAlerts && stats.recent_alerts.length > 0 && (
                <button type="button" onClick={deleteAllAlerts} className="text-[11px] font-semibold text-slate-500 underline-offset-2 transition-colors hover:text-red-600 hover:underline" data-testid="alerts-delete-all-btn">Elimina tutti</button>
              )}
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
          </div>
          {stats.recent_alerts.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400">{t("auto.nessun_alert")}</div>
          ) : (
            <ul className={`space-y-3 ${allAlerts ? "max-h-[520px] overflow-y-auto pr-1" : ""}`} data-testid="alerts-list">
              {(allAlerts || stats.recent_alerts).map((a) => a.type === "email_no_serial" ? (
                <li key={a.id} className="relative rounded-sm border border-amber-200 bg-amber-50 p-3 pr-8 text-xs" data-testid={`alert-email-${a.id}`}>
                  <AlertDeleteBtn id={a.id} show={canDeleteAlerts} onDelete={deleteAlert} />
                  <div className="font-bold text-amber-800">Mail "{a.subject}"</div>
                  <div className="mt-1 text-amber-900">
                    da <b>{a.sender_name || a.sender_email}</b>: non è stato possibile recuperare il seriale. Scaricare il materiale manualmente.
                  </div>
                </li>
              ) : (
                <li key={a.id} className="relative rounded-sm border border-red-100 bg-red-50 p-3 pr-8 text-xs" data-testid={`alert-item-${a.id}`}>
                  <AlertDeleteBtn id={a.id} show={canDeleteAlerts} onDelete={deleteAlert} />
                  <div className="mono font-medium text-red-700">{a.serial}</div>
                  <div className="mt-1 text-red-800">
                    {t("common.assigned_to")} <b>{a.expected_owner_name || "?"}</b>, {t("auto.installato_da_prefix")} <b>{a.actual_owner_name || "?"}</b>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {(stats.alerts_count || 0) > 5 && (
            <button type="button" onClick={toggleAllAlerts} className="mt-3 w-full rounded-sm border border-slate-200 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50" data-testid="alerts-show-all-btn">
              {allAlerts ? "Mostra solo gli ultimi 5" : `Mostra tutti (${stats.alerts_count})`}
            </button>
          )}
        </div>
        )}
      </div>

      {isTecnico && history && history.months.length > 0 && (
        <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid="earnings-history-card">
          <div className="mb-1 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.storia_guadagni_6mesi")}</div>
              <div className="mt-1 text-xs text-slate-400">{t("auto.quota_mensile_calcolata_con_split_cotecnici")}</div>
            </div>
            <TrendingUp className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="mt-2 h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={history.months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => `€${v}`} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 12 }}
                  formatter={(v, name, p) => [`€ ${Number(v).toFixed(2)}`, `${p.payload.count} lavor.`]}
                  labelStyle={{ color: "#0f172a", fontWeight: 700 }}
                />
                {stats.monthly_goal > 0 && (
                  <ReferenceLine y={stats.monthly_goal} stroke="#0052FF" strokeDasharray="4 4" label={{ value: `Meta €${stats.monthly_goal}`, fill: "#0052FF", fontSize: 10, position: "insideTopRight" }} />
                )}
                <ChartBar dataKey="total" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={60} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {(() => {
            const totals = history.months.map((m) => m.total);
            const nonZero = totals.filter((tech) => t > 0);
            const avg = nonZero.length ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length : 0;
            const best = Math.max(0, ...totals);
            return (
              <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-2 text-center">
                  <div className="uppercase tracking-widest text-slate-500">Media 6m</div>
                  <div className="mt-0.5 font-mono font-bold text-slate-900">€ {avg.toFixed(2)}</div>
                </div>
                <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-2 text-center">
                  <div className="uppercase tracking-widest text-emerald-700">Miglior mese</div>
                  <div className="mt-0.5 font-mono font-bold text-emerald-800">€ {best.toFixed(2)}</div>
                </div>
                <div className="rounded-sm border border-blue-200 bg-blue-50 p-2 text-center">
                  <div className="uppercase tracking-widest text-[#0052FF]">Totale 6m</div>
                  <div className="mt-0.5 font-mono font-bold text-slate-900">€ {totals.reduce((a, b) => a + b, 0).toFixed(2)}</div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {isTecnico && myKpi && (
        <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid="my-kpi-card">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Il mio KPI — Ultimi 30 giorni</div>
              <div className="mt-1 text-xs text-slate-400">Assegnati vs chiusi positivi</div>
            </div>
            <Activity className="h-5 w-5 text-[#0052FF]" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-center" data-testid="my-kpi-assigned">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Assegnati</div>
              <div className="mt-1 font-mono text-2xl font-bold text-slate-900">{myKpi.totals.assigned}</div>
            </div>
            <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-center" data-testid="my-kpi-closed">
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Chiusi positivi</div>
              <div className="mt-1 font-mono text-2xl font-bold text-emerald-800">{myKpi.totals.closed}</div>
            </div>
            <div className={`rounded-sm border p-3 text-center ${
              myKpi.totals.kpi === null || myKpi.totals.kpi === undefined ? "border-slate-200 bg-slate-50" :
              myKpi.totals.kpi >= 80 ? "border-emerald-300 bg-emerald-100" :
              myKpi.totals.kpi >= 50 ? "border-amber-300 bg-amber-100" : "border-red-300 bg-red-100"
            }`} data-testid="my-kpi-percent">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">KPI</div>
              <div className="mt-1 font-mono text-2xl font-bold text-slate-900">
                {myKpi.totals.kpi === null || myKpi.totals.kpi === undefined ? "—" : `${myKpi.totals.kpi}%`}
              </div>
            </div>
          </div>
          {myKpi.daily.length > 0 && (
            <div className="mt-4 h-40 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={myKpi.daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 12 }} />
                  <ChartBar dataKey="assigned" name="Assegnati" fill="#0052FF" radius={[2, 2, 0, 0]} />
                  <ChartBar dataKey="closed" name="Chiusi" fill="#10b981" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {/* KO breakdown */}
          {(myKpi.ko_breakdown || []).length > 0 && (
            <div className="mt-4 space-y-2 border-t border-slate-200 pt-3" data-testid="my-kpi-ko-breakdown">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Distribuzione esiti (su {myKpi.totals.assigned} assegnati)</div>
                <div className="text-[10px] font-mono text-slate-500">{myKpi.totals.ko} KO totali</div>
              </div>
              {/* OK row */}
              <div className="flex items-center gap-2">
                <div className="w-32 text-xs text-emerald-800">Chiusi positivi</div>
                <div className="relative h-4 flex-1 overflow-hidden rounded-sm bg-slate-100">
                  <div className="absolute inset-y-0 left-0 bg-emerald-500 transition-all" style={{ width: `${myKpi.totals.ok_pct || 0}%` }} />
                </div>
                <div className="w-16 text-right font-mono text-xs font-bold text-emerald-700">{(myKpi.totals.ok_pct || 0).toFixed(1)}%</div>
              </div>
              {/* KO rows */}
              {(myKpi.ko_breakdown || []).map((k) => (
                <div key={k.reason} className="flex items-center gap-2" data-testid={`ko-row-${k.reason}`}>
                  <div className="w-32 truncate text-xs text-red-800" title={k.reason}>{k.reason}</div>
                  <div className="relative h-4 flex-1 overflow-hidden rounded-sm bg-slate-100">
                    <div className="absolute inset-y-0 left-0 bg-red-500 transition-all" style={{ width: `${k.pct_of_assigned}%` }} />
                  </div>
                  <div className="w-16 text-right font-mono text-xs font-bold text-red-700">{k.pct_of_assigned.toFixed(1)}%</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {canSeeRevenue && (
      <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid="production-by-committente-card">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.produzione_per_committente")}</div>
            <div className="mt-1 text-xs text-slate-400">
              {prodByComm ? (
                prodByComm.meta.custom_range
                  ? `Range: ${prodByComm.meta.current_period} — vs finestra prec. ${prodByComm.meta.previous_period}`
                  : `Mese corrente ${prodByComm.meta.current_period} — giorno ${prodByComm.meta.days_elapsed}/${prodByComm.meta.days_in_month}`
              ) : "…"}
            </div>
          </div>
          {/* Filtro date custom */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={prodFrom}
              onChange={(e) => setProdFrom(e.target.value)}
              className="rounded-sm border border-slate-300 px-2 py-1 text-xs"
              data-testid="prod-date-from"
              title="Dal"
            />
            <span className="text-xs text-slate-400">→</span>
            <input
              type="date"
              value={prodTo}
              onChange={(e) => setProdTo(e.target.value)}
              className="rounded-sm border border-slate-300 px-2 py-1 text-xs"
              data-testid="prod-date-to"
              title="Al"
            />
            <button
              onClick={applyProdFilter}
              disabled={!prodFrom || !prodTo || prodLoading}
              className="rounded-sm bg-[#0052FF] px-3 py-1 text-xs font-semibold text-white hover:bg-[#0040CC] disabled:opacity-40"
              data-testid="prod-apply-filter"
            >
              Applica
            </button>
            {prodByComm?.meta?.custom_range && (
              <button
                onClick={resetProdFilter}
                className="rounded-sm border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                data-testid="prod-reset-filter"
                title="Torna al mese corrente"
              >
                ✕ Reset
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider">
            <span className="flex items-center gap-1 rounded-sm bg-slate-100 px-2 py-1 text-slate-600">
              <span className="h-2 w-2 rounded-full bg-slate-400" /> Prec.
            </span>
            <span className="flex items-center gap-1 rounded-sm bg-blue-50 px-2 py-1 text-[#0052FF]">
              <span className="h-2 w-2 rounded-full bg-[#0052FF]" /> Corrente
            </span>
            {!prodByComm?.meta?.custom_range && (
              <span className="flex items-center gap-1 rounded-sm bg-emerald-50 px-2 py-1 text-emerald-700">
                <Target className="h-2.5 w-2.5" /> Proiezione
              </span>
            )}
          </div>
        </div>

        {!prodByComm && <div className="py-4 text-center text-sm text-slate-400">Caricamento…</div>}

        {prodByComm && (
          <>
            {/* Totals row */}
            <div className={`mb-5 grid gap-3 ${prodByComm.meta.custom_range ? "grid-cols-2" : "grid-cols-3"}`}>
              <div className="rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="prod-total-previous">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  {prodByComm.meta.custom_range ? "Periodo prec." : "Mese precedente"}
                </div>
                <div className="mt-1 font-mono text-lg font-semibold text-slate-700">€ {prodByComm.totals.previous.toFixed(2)}</div>
              </div>
              <div className="rounded-sm border border-blue-200 bg-blue-50 p-3" data-testid="prod-total-current">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#0052FF]">
                  {prodByComm.meta.custom_range ? "Range selezionato" : "Corrente (MTD)"}
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <div className="font-mono text-lg font-semibold text-[#0052FF]">€ {prodByComm.totals.current.toFixed(2)}</div>
                  <DeltaBadge value={prodByComm.totals.delta_pct} />
                </div>
              </div>
              {!prodByComm.meta.custom_range && (
                <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3" data-testid="prod-total-projection">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Proiezione fine mese</div>
                  <div className="mt-1 font-mono text-lg font-semibold text-emerald-700">€ {(prodByComm.totals.projection ?? 0).toFixed(2)}</div>
                </div>
              )}
            </div>

            {/* Per-committente rows */}
            {prodByComm.items.length === 0 && (
              <div className="py-4 text-center text-sm text-slate-400">{t("auto.nessun_committente_configurato")}</div>
            )}
            <div className="space-y-3">
              {prodByComm.items.map((it) => {
                const maxBar = Math.max(...prodByComm.items.map((x) => Math.max(x.current_total, x.previous_total, x.projection || 0)), 1);
                return (
                  <div key={it.committente_id} className="rounded-sm border border-slate-100 p-3" data-testid={`prod-comm-${it.committente_id}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-semibold text-slate-900">{it.committente_name}</div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">
                          <span className="font-mono">{it.current_count}</span> lav.
                        </span>
                        <DeltaBadge value={it.delta_pct} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Bar label="Prec." value={it.previous_total} max={maxBar} color="bg-slate-400" />
                      <Bar label="Corr." value={it.current_total} max={maxBar} color="bg-[#0052FF]" />
                      {it.projection != null && (
                        <Bar label="Proj." value={it.projection} max={maxBar} color="bg-emerald-500" dashed />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      )}

      {canSeeRevenue && (
      <div className="rounded-sm border border-slate-200 bg-white p-6">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Top produzione mese corrente</div>
          <a href="/ranking" className="text-xs font-medium text-[#0052FF] hover:underline">Vedi ranking completo →</a>
        </div>
        {ranking.length === 0 && <div className="py-4 text-center text-sm text-slate-400">{t("auto.nessuna_lavorazione_ancora_caricata")}</div>}
        <div className="space-y-2">
          {ranking.slice(0, 5).map((r, i) => {
            const max = Math.max(...ranking.map((x) => x.total), 1);
            return (
              <div key={r.technician_id} className="flex items-center gap-3">
                <div className={`grid h-7 w-7 place-items-center rounded-sm font-mono text-xs font-bold ${i === 0 ? "bg-orange-500 text-white" : i === 1 ? "bg-slate-400 text-white" : i === 2 ? "bg-amber-700 text-white" : "bg-slate-200 text-slate-700"}`}>{i + 1}</div>
                <div className="min-w-32 flex-shrink-0 text-sm font-medium text-slate-900">{r.technician_name}</div>
                <div className="h-2 flex-1 overflow-hidden rounded-sm bg-slate-100">
                  <div className="h-full bg-[#0052FF]" style={{ width: `${(r.total / max) * 100}%` }} />
                </div>
                <div className="w-24 text-right font-mono text-sm font-semibold text-slate-900">€ {Number(r.total).toFixed(2)}</div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      <div className="rounded-sm border border-slate-200 bg-white p-6">
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Suggerimento</div>
        <div className="mt-2 flex items-start gap-3">
          <ClipboardList className="mt-0.5 h-5 w-5 text-slate-500" />
          <p className="text-sm text-slate-700">
            Prima di generare il BEF di fine mese, verifica che tutte le lavorazioni siano state caricate e che ogni seriale installato risulti scaricato dal magazzino viaggiante del tecnico.
          </p>
        </div>
      </div>
    </div>
  );
}
